import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import { refreshMediaToken, clearMediaToken } from './media';
import InviteAccept from './components/InviteAccept';
import './App.css';

// Set up axios defaults
const API_URL = '/api';
axios.defaults.baseURL = API_URL;

// Helper: pick a sensible current-org from the user's memberships and prime
// the axios header so Dashboard's first fetch already carries it. Avoids a
// race between OrgProvider's effect and Dashboard's useEffect.
function primeOrgHeader(memberships) {
  if (!memberships || memberships.length === 0) {
    delete axios.defaults.headers.common['X-Org-Id'];
    return;
  }
  const stored = localStorage.getItem('currentOrgId');
  const valid = stored && memberships.some(m => m.organization._id === stored);
  const initial = valid ? stored : memberships[0].organization._id;
  axios.defaults.headers.common['X-Org-Id'] = initial;
  localStorage.setItem('currentOrgId', initial);
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      primeOrgHeader(response.data.memberships);
      // Uploads are private (WR-014) and are fetched with a short-lived
      // media token. Obtained before the app renders so no avatar or
      // attachment ever mounts unsigned.
      await refreshMediaToken();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  // After login, immediately call /auth/me to pick up memberships (the login
  // response itself doesn't include them).
  const handleLogin = async (_userData, token) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    try {
      const response = await axios.get('/auth/me');
      primeOrgHeader(response.data.memberships);
      await refreshMediaToken();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user after login:', error);
    }
  };

  const handleUpdateUser = (updatedUser) => {
    // Preserve memberships when other parts of the app update the user
    // (e.g. EditProfileModal returns just the user without memberships).
    setUser(prev => ({
      ...updatedUser,
      memberships: updatedUser.memberships ?? prev?.memberships ?? [],
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentOrgId');
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Org-Id'];
    clearMediaToken();
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />}
        />
        <Route
          path="/register"
          element={user ? <Navigate to="/" /> : <Register onLogin={handleLogin} />}
        />
        <Route
          path="/forgot-password"
          element={user ? <Navigate to="/" /> : <ForgotPassword />}
        />
        <Route
          path="/reset-password/:token"
          element={user ? <Navigate to="/" /> : <ResetPassword />}
        />
        <Route
          path="/invite/:token"
          element={<InviteAccept user={user} />}
        />
        <Route
          path="/*"
          element={user ? <Dashboard user={user} onLogout={handleLogout} onUpdateUser={handleUpdateUser} /> : <Navigate to="/login" />}
        />
      </Routes>
    </Router>
  );
}

export default App;
